/**
 * What each coupon action really does in Shopify. Every code, creator or
 * campaign, has a Shopify discount of its own: Deactivate turns that discount
 * off and can be undone, Delete removes it for good. A campaign code is used by
 * every creator on the campaign; a creator code only by that creator.
 */
export type CodeScope = 'creator' | 'campaign';

const codes = (n: number) => `${n} code${n === 1 ? '' : 's'}`;
const uses = (n: number) => `${n} use${n === 1 ? '' : 's'}`;

export const shopifyActionsCopy = {
  templateNote:
    'Codes that already exist keep their terms. To change one, use Edit discount on the Coupons tab: it changes only that code, and says why when it can’t be used. Use Change code to give a creator a different code.',
  templateNoteCampaignWide:
    'Codes that already exist keep their terms. To change one, use Edit discount on the Coupons tab: it changes only that code, and says why when it can’t be used. Use Change code to give this campaign a different code.',
  activeNote:
    'Active means Shopify accepts the code. A cart gets the discount only when it meets these rules.',
  cartRule: 'A cart gets the discount only when it meets these rules.',
  bxgyCartRule:
    'Customers must add both the qualifying buy items and the reward items to their cart. A discount link applies the code; it does not add reward items.',
  /** Usage limits are counted on the code's own discount. */
  totalUses: (n: number) => `${n.toLocaleString()} total use${n === 1 ? '' : 's'}`,
  bulkNote: 'Discovr won’t message the creators. Tell them first if they’re already sharing.',
  /** A creator whose only code is deactivated: Generate skips them. */
  inactivePicker: {
    badge: 'Deactivated',
    note: 'Their code is deactivated. Reactivate it under Inactive codes instead.',
  },
} as const;

/** Creator names the missing-discount title shows before "+N more". */
const MISSING_DISCOUNT_PREVIEW_COUNT = 3;

export interface MissingDiscountCopy {
  title: string;
  detail: string;
  /** Creators past the preview, behind the "+N more" toggle. */
  hiddenCount: number;
  toggleLabel: (expanded: boolean) => string;
}

/** Creators with no code at all. A code is optional: they can still go live. */
export function missingDiscountCopy(names: string[], canGenerate = true): MissingDiscountCopy {
  const hiddenCount = Math.max(names.length - MISSING_DISCOUNT_PREVIEW_COUNT, 0);
  return {
    title: `Missing discount: ${names.slice(0, MISSING_DISCOUNT_PREVIEW_COUNT).join(', ')}`,
    hiddenCount,
    toggleLabel: (expanded) => (expanded ? 'Show less' : `+${hiddenCount} more`),
    detail: `${names.length === 1 ? 'This creator has' : 'These creators have'} no Shopify code yet. They can still go live, but their posts won’t carry a discount code ${canGenerate ? 'until you generate one.' : 'until the brand generates one.'}`,
  };
}

/** The review step before Generate creates anything in the merchant's store. */
export const generateReviewCopy = {
  title: 'Review before creating codes',
  titleForCreator: (name: string) => `Review before creating a code for ${name}`,
  description: 'Nothing is created in Shopify until you confirm.',
  creatorDelivery:
    "Discovr emails a creator their code when their brief is sent. Codes made after that aren't sent; copy the code or link from Coupons and share it yourself.",
  creatorsHeading: (n: number) => `${n} ${n === 1 ? 'creator gets' : 'creators get'} a code`,
  codeNote:
    'Codes follow this pattern. If a code is already taken, a number is added at the end.',
  termsHeading: 'Discount terms',
  termsNote: 'Each code is its own Shopify discount with these terms.',
  skippedHeading: (n: number) => `${n} skipped`,
  skipReason: {
    has_coupon: 'Already has a code',
    has_inactive_coupon: 'Code deactivated',
    has_renamed_coupon: 'Code changed in Shopify',
    on_hold: 'On hold',
  },
  cancel: 'Cancel',
  confirm: (n: number) => `Create ${n} ${n === 1 ? 'code' : 'codes'}`,
  creating: 'Creating…',
  resultTitle: 'Codes created',
  createdHeading: (n: number) => `${n} ${n === 1 ? 'code' : 'codes'} created`,
  nothingCreated: 'No new codes were created.',
  partialSummary: (created: number, remaining: number) =>
    `Created ${created} ${created === 1 ? 'discount code' : 'discount codes'}, ${remaining} left. Run Generate again.`,
  failedNote: (n: number) => `${n} couldn’t be created. Run Generate again to retry.`,
  generateAgain: 'Generate again',
  resultSkipReason: {
    already_live: 'Already has a code',
    deactivated: 'Code deactivated',
    in_progress: 'Code being created',
    on_hold: 'On hold',
    renamed: 'Code changed in Shopify',
  },
  done: 'Done',
  openStore: 'Open store with this code',
  sendBriefs: 'Send briefs',
} as const;

export interface BulkActionCopy {
  title: string;
  description: string;
  warning: string;
  statLabel: string;
  confirmLabel: string;
  confirmWord: 'DELETE' | 'DEACTIVATE';
  finality: string;
}

export function bulkActionCopy(scope: CodeScope, n: number): BulkActionCopy {
  const description =
    scope === 'creator'
      ? 'Each creator code has its own Shopify discount, so only the selected creators’ codes are affected.'
      : 'A campaign code is shared by every creator on this campaign.';
  const who =
    scope === 'creator' ? 'for those creators' : 'for every creator who shares them';
  return {
    title: `Deactivate ${n} ${scope} ${n === 1 ? 'code' : 'codes'}?`,
    description,
    warning: `Their Shopify discounts are deactivated, so the codes stop working at checkout ${who}. They stay under Inactive codes with their sales.`,
    statLabel: 'Codes deactivated',
    confirmLabel: `Deactivate ${codes(n)}`,
    confirmWord: 'DEACTIVATE',
    finality:
      n === 1
        ? 'Reactivate it from Inactive codes when you want it to work again. Shopify dates and limits still apply.'
        : 'Reactivate them from Inactive codes when you want them to work again. Shopify dates and limits still apply.',
  };
}

/** A mixed selection gets one button per kind, so each names what it does. */
export function bulkToolbarLabel(scope: CodeScope, n: number, mixed: boolean): string {
  if (!mixed) return 'Deactivate';
  return `Deactivate ${n} ${scope} ${n === 1 ? 'code' : 'codes'}`;
}

export interface RevokeInput {
  scope: CodeScope;
  code: string;
  usesCount: number;
  /** Creators using a campaign code. */
  sharedBy?: number;
}

export interface RevokeCopy {
  title: string;
  description: string;
  warning?: string;
  confirmLabel: string;
  confirmWord: 'DELETE' | 'DEACTIVATE';
  finality: string;
}

export function revokeCopy({ scope, code, usesCount, sharedBy = 0 }: RevokeInput): RevokeCopy {
  const usesLine =
    usesCount > 0
      ? `Shopify counts ${uses(usesCount)} so far (updated periodically). Links already shared stop giving a discount until you reactivate it.`
      : null;
  const reach =
    scope === 'creator'
      ? 'Only this creator’s code is affected; other creators’ codes keep working.'
      : sharedBy > 0
        ? `This code is shared by ${sharedBy} creator${sharedBy === 1 ? '' : 's'} on this campaign. Deactivating it stops it working for all of them.`
        : null;
  const warning = [reach, usesLine].filter(Boolean).join(' ') || undefined;
  return {
    title: scope === 'creator' ? 'Deactivate this creator’s code?' : 'Deactivate this code?',
    description: `${code} and its link stop giving a discount right away.`,
    ...(warning ? { warning } : {}),
    confirmLabel: 'Deactivate',
    confirmWord: 'DEACTIVATE',
    finality:
      'Reactivate it from Inactive codes when you want it to work again. Shopify dates and limits still apply.',
  };
}

export type LimitScope =
  | { kind: 'template'; campaignWide: boolean }
  | { kind: 'discount'; campaignWide: boolean };

export interface LimitScopeCopy {
  total: string;
  oncePerCustomer: string;
}

/** Usage limits sit on the code's own Shopify discount. */
export function usageLimitScopeCopy(scope: LimitScope): LimitScopeCopy {
  if (scope.campaignWide) {
    return {
      total: 'Counts every use of the campaign code, which every creator shares.',
      oncePerCustomer: 'Each customer can use the campaign code once.',
    };
  }
  if (scope.kind === 'template') {
    return {
      total: 'A limit for each creator code: every creator code gets its own Shopify discount.',
      oncePerCustomer: 'Each customer can use each creator’s code once.',
    };
  }
  return {
    total: 'Counts every use of this creator’s code. Other creators’ codes have their own limits.',
    oncePerCustomer: 'Each customer can use this creator’s code once.',
  };
}

export interface ChangeCodeCopy {
  oldCode: string;
  newCode: string;
}

export function changeCodeCopy(scope: CodeScope): ChangeCodeCopy {
  const share = scope === 'campaign' ? 'your creators' : 'the creator';
  return {
    oldCode:
      'Once the new code is created, Discovr deactivates this one in Shopify and its link stops giving a discount. If that fails, you’re told so you can deactivate it in Shopify admin. It stays under Replaced codes with its past orders, and you can usually reactivate it there.',
    newCode: `The new code gets its own Shopify discount, so its use count starts at zero and any total usage limit counts again from the start. Share the new code and link with ${share}.`,
  };
}

export type AvailabilityOutcome =
  | { state: 'off' }
  | { state: 'active'; end?: string; startsNow?: boolean }
  | { state: 'scheduled'; start: string; end?: string };

export type AvailabilityPreviewInput = AvailabilityOutcome & {
  /** The code was deactivated when the form opened. */
  wasOff: boolean;
  scope: 'campaign' | 'creator';
  /** Codes on the discount the edit changes. */
  count: number;
};

/** The state Shopify will give the codes from the dates on the form. */
export function availabilityPreview(p: AvailabilityPreviewInput): string {
  const subject = p.count === 1 ? 'this code' : `these ${p.count} codes`;
  const until = (end?: string) => (end ? `until ${end}` : 'with no end date');
  const unlessLive = `unless another code for this ${p.scope} is live; then the save is refused.`;
  if (p.state === 'off') {
    return p.wasOff
      ? 'After saving, the code stays deactivated.'
      : `After saving, ${subject} stop${p.count === 1 ? 's' : ''} working.`;
  }
  if (p.state === 'scheduled') {
    const end = p.end ? ` and end on ${p.end}` : '';
    return p.wasOff
      ? `After saving, the code is scheduled to start on ${p.start}${end}, ${unlessLive}`
      : `After saving, ${subject} won’t work at checkout until ${p.start}${p.end ? `, and end${p.count === 1 ? 's' : ''} on ${p.end}` : ''}.`;
  }
  if (p.wasOff) {
    return `After saving, the code is active again right away, ${until(p.end)}, ${unlessLive}`;
  }
  const verb = p.startsNow ? 'start' : 'keep';
  const s = p.count === 1 ? 's' : '';
  return p.startsNow
    ? `After saving, ${subject} ${verb}${s} working right away, ${until(p.end)}.`
    : `After saving, ${subject} ${verb}${s} working${p.end ? ` until ${p.end}` : ', with no end date'}.`;
}
